import { DragEvent, FC, useEffect, useState } from 'react'
import useCourseThemes from '@hooks/useCourseThemes'
import useMe from '@hooks/useMe'
import { useReorderCourseChapterThemesMutation } from '@services/courseThemes'
import { createInitFilter } from '@utils/filters'
import { CourseChapter } from '@models/courseChapter'
import { CourseThemeListItemServerResponse } from '@models/courseTheme'
import AddThemeButton from './AddThemeButton'
import DeleteChapterLink from './DeleteChapterLink'
import EditChapterLink from './EditChapterLink'
import { EditButtonsWrapper, StyledThemeTitle, ThemeWrapper } from './styled'
import SubAccordion from './SubAccrodion'
import Theme from './Theme'

type Props = {
  onExpanded: (index: number) => void
  onChangeSize: () => void
  courseIri: string
  index: number
  initialExpanded: boolean
  draggable?: boolean
  isDragging?: boolean
  onDragStart?: (e: DragEvent<HTMLElement>) => void
  onDragEnd?: (e: DragEvent<HTMLElement>) => void
  onDragOver?: (e: DragEvent<HTMLElement>) => void
  onDrop?: (e: DragEvent<HTMLElement>) => void
} & CourseChapter

const sortByOrder = (
  a: CourseThemeListItemServerResponse,
  b: CourseThemeListItemServerResponse
) => {
  const aOrder = a.sortOrder ?? 0
  const bOrder = b.sortOrder ?? 0

  if (aOrder === bOrder) {
    return a.id - b.id
  }

  return aOrder - bOrder
}

const Chapter: FC<Props> = ({
  onChangeSize,
  courseIri,
  onExpanded,
  index,
  initialExpanded,
  draggable = false,
  isDragging = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  ...chapter
}) => {
  const { id, title } = chapter
  const { isAdmin } = useMe()

  const { data } = useCourseThemes(createInitFilter('courseChapter', id))
  const [reorderThemes] = useReorderCourseChapterThemesMutation()

  const [localThemes, setLocalThemes] = useState<
    CourseThemeListItemServerResponse[]
  >([])
  const [draggedThemeIndex, setDraggedThemeIndex] = useState<number | null>(null)

  const chapterIri = chapter['@id']

  useEffect(() => {
    if (data) {
      setLocalThemes([...data.items].sort(sortByOrder))
    }
  }, [data])

  useEffect(() => {
    onChangeSize()
  }, [localThemes.length])

  const handleThemeDragStart = (
    e: DragEvent<HTMLElement>,
    themeIndex: number
  ) => {
    setDraggedThemeIndex(themeIndex)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(themeIndex))
  }

  const handleThemeDragOver = (e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleThemeDrop = async (
    e: DragEvent<HTMLElement>,
    dropIndex: number
  ) => {
    e.preventDefault()
    e.stopPropagation()

    if (draggedThemeIndex === null || draggedThemeIndex === dropIndex) {
      setDraggedThemeIndex(null)

      return
    }

    const prevThemes = localThemes
    const newThemes = [...localThemes]
    const [moved] = newThemes.splice(draggedThemeIndex, 1)
    newThemes.splice(dropIndex, 0, moved)

    setLocalThemes(newThemes)
    setDraggedThemeIndex(null)

    const themeIds = newThemes.map((theme) => theme.id)

    try {
      await reorderThemes({ chapterId: id, themeIds }).unwrap()
    } catch {
      setLocalThemes(prevThemes)
    }
  }

  const handleThemeDragEnd = () => {
    setDraggedThemeIndex(null)
  }

  return (
    <SubAccordion
      initialExpanded={initialExpanded}
      index={index}
      onExpanded={onExpanded}
      onChangeSize={onChangeSize}
      title={title}
      draggable={draggable}
      isDragging={isDragging}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      Body={
        <ThemeWrapper
          onDragOver={isAdmin ? handleThemeDragOver : undefined}
          onDrop={
            isAdmin
              ? (e: DragEvent<HTMLElement>) =>
                  handleThemeDrop(e, localThemes.length)
              : undefined
          }
        >
          {localThemes.length > 0 && (
            <StyledThemeTitle mySize="h2">Темы</StyledThemeTitle>
          )}
          {localThemes.map((theme, themeIndex) => (
            <Theme
              {...theme}
              key={theme.id}
              chapterIri={chapterIri}
              courseIri={courseIri}
              isDragging={draggedThemeIndex === themeIndex}
              onDragStart={(e: DragEvent<HTMLElement>) =>
                handleThemeDragStart(e, themeIndex)
              }
              onDragEnd={handleThemeDragEnd}
              onDragOver={handleThemeDragOver}
              onDrop={(e: DragEvent<HTMLElement>) =>
                handleThemeDrop(e, themeIndex)
              }
            />
          ))}
          {isAdmin && <AddThemeButton chapterIri={chapterIri} />}
        </ThemeWrapper>
      }
      height={150 + localThemes.length * 40}
      rightHeaderContent={
        isAdmin && (
          <EditButtonsWrapper>
            <EditChapterLink courseIri={courseIri} chapter={chapter} />
            <DeleteChapterLink chapter={chapter} />
          </EditButtonsWrapper>
        )
      }
    />
  )
}

export default Chapter
